import { Button, Modal, Row, Space, Table, Tooltip } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import { useStoreActions, useStoreState } from '../../../store';
import type { Key } from 'react';
import { useEffect } from 'react';
import getGatewayUri from '../../../store/services/getGatewayUri';
import axios from 'axios';
import { getToken } from '../../../store/session';
import { DeleteOutlined, FileOutlined, ReloadOutlined } from '@ant-design/icons';
import { ShipiumTag } from '../../shared/ShipiumTag';
import { differenceInMinutes, format } from 'date-fns';
import { safeDateFormat } from '../../../pages/Simulation/utils';
import { getSimulationUploadErrorMessage } from './simulation-utils';
import { useMutation } from '@tanstack/react-query';
import { simulationService } from '~/services';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { useSession } from '~/hooks/useSession';
import { useSimulationUploads } from '~/hooks/simulation/useSimulationUploads';
import type { SimulationServiceVersion } from '~/store/simulation/types';
import { type UnifiedSimulationUpload, isNewUpload, getFileUploadId } from './uploadAdapter';

export interface SimulationUploadsTableProps {
  onSelect: (val: any) => void;
  selectedKeys?: Key[];
  allowNonHistorical?: boolean;
  version: SimulationServiceVersion;
}

export const SimulationUploadsTable = ({
  onSelect,
  selectedKeys,
  allowNonHistorical = true,
  version,
}: SimulationUploadsTableProps) => {
  const userMap = useStoreState((state) => state.users.users);
  const fetchUsers = useStoreActions((actions) => actions.users.fetchUsers);

  useEffect(() => {
    fetchUsers(undefined);
  }, [fetchUsers]);
  const partnerId = useStoreState((state) => state.session.user?.sessionPartnerId) || '';
  const { isShipiumView } = useSession();
  const { data: simulationUploads, refetch, isFetching } = useSimulationUploads(version, partnerId, isShipiumView);

  const deleteLegacyUpload = useMutation(useMutationInvalidator(simulationService.uploads.delete));
  const deleteNewUpload = useMutation(useMutationInvalidator(simulationService.fileUploads.delete));

  const columns: ColumnType<UnifiedSimulationUpload>[] = [
    {
      title: 'File name',
      render: (row) => {
        return <div style={{ wordBreak: 'break-word' }}>{row.originalFileName}</div>;
      },
    },
    {
      title: 'Status',
      render: (row: UnifiedSimulationUpload) => {
        if (row.details?.errorType) {
          return (
            <ShipiumTag color="Coral">
              <Tooltip title={getSimulationUploadErrorMessage(row.details.errorType).title}>Invalid</Tooltip>
            </ShipiumTag>
          );
        } else if (row.details?.processed) {
          if (row.details.includesHistorical) {
            return (
              <Space direction="vertical">
                <ShipiumTag color="Teal">Processed</ShipiumTag>
                <ShipiumTag color="Blue">Includes historical data</ShipiumTag>
              </Space>
            );
          } else {
            return (
              <Space direction="vertical">
                <ShipiumTag color="Teal">Processed</ShipiumTag>
                <ShipiumTag color="Gold"> No historical data</ShipiumTag>
              </Space>
            );
          }
        } else if (!isNewUpload(row) && differenceInMinutes(new Date(), new Date(row.createdAt)) > 5) {
          // Only show "Failed" timeout for legacy uploads; new uploads have explicit status
          return <ShipiumTag color="Coral">Failed</ShipiumTag>;
        }
        return <ShipiumTag color="Grey">Processing</ShipiumTag>;
      },
    },
    {
      title: 'Shipment Count',
      render: (row) => {
        return !row?.details?.shipmentCount ? 'Unknown' : row?.details?.shipmentCount;
      },
    },
    {
      title: 'Start Date',
      render: (row) => {
        return !row?.details?.minShipDate ? (
          'Unknown'
        ) : (
          <Tooltip title="The earliest shipment date that we have detected in the file">
            {safeDateFormat(row.details.minShipDate)}
          </Tooltip>
        );
      },
    },
    {
      title: 'End Date',
      render: (row) => {
        return !row?.details?.maxShipDate ? (
          'Unknown'
        ) : (
          <Tooltip title="The latest shipment date that we have detected in the file">
            {safeDateFormat(row?.details?.maxShipDate)}
          </Tooltip>
        );
      },
    },
    {
      title: 'Upload Date',
      render: (row) => {
        return !row?.createdAt ? 'Unknown' : format(new Date(row?.createdAt), 'PPPP');
      },
    },
    {
      title: 'Uploaded by',
      render: (row) => {
        const uploadUser = userMap[row.userId];
        return (
          <>
            {uploadUser ? (
              <p>
                {uploadUser.givenName} {uploadUser.surname}
              </p>
            ) : (
              <p> Unknown</p>
            )}
          </>
        );
      },
    },
    {
      title: 'Download',
      render: (row) => {
        return (
          <Button
            type="primary"
            onClick={() => {
              // I think it makes the most sense NOT
              // to pipe any of this upload/dowload stuff through redux
              // given that none of this stuff is stateful
              axios({
                method: 'POST',
                url: `${getGatewayUri()}/getPresignedDownloadUrl`,
                data: {
                  subKey: row.s3Key.split('/').slice(1).join('/'),
                },
                headers: { authorization: `Bearer ${getToken()}` },
                withCredentials: true,
              }).then((val) => {
                const link = document.createElement('a');
                link.setAttribute('href', val.data.downloadUrl);
                link.setAttribute('download', `${row.originalFileName}`);
                document.body.appendChild(link);
                link.click();
              });
            }}
          >
            Download <FileOutlined />
          </Button>
        );
      },
    },
    {
      title: 'Delete',
      render: (row: UnifiedSimulationUpload) => {
        const fileUploadId = getFileUploadId(row);
        const isDeleting = deleteLegacyUpload.isLoading || deleteNewUpload.isLoading;
        return (
          <Button
            onClick={async () => {
              Modal.confirm({
                title: 'Delete Upload',
                content: `Are you sure you want to delete "${row.originalFileName}"?`,
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: async () => {
                  if (fileUploadId) {
                    await deleteNewUpload.mutateAsync(fileUploadId);
                  } else {
                    await deleteLegacyUpload.mutateAsync({ partnerId: row.partnerId, s3Key: row.s3Key });
                  }
                },
              });
            }}
            shape="circle"
            icon={<DeleteOutlined />}
            danger
            loading={isDeleting}
          />
        );
      },
      align: 'center',
    },
  ];
  return (
    <>
      {refetch && (
        <Row justify="end" style={{ marginBottom: 8 }}>
          {/* Manual refresh to pick up uploads validated on the Uploads page (cached list). */}
          <Button size="small" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        </Row>
      )}
      <Table
        rowSelection={{
          type: 'checkbox',
          hideSelectAll: true,
          selectedRowKeys: selectedKeys,
          onChange: (selectedRowKeys: React.Key[], selectedRows: UnifiedSimulationUpload[]) => {
            onSelect(selectedRows);
          },
          getCheckboxProps: (record: UnifiedSimulationUpload) => ({
            disabled:
              !record.details?.processed ||
              !!record.details.errorType ||
              // disable if file not processed or simulation is historical and file is not
              (!allowNonHistorical && !record.details.includesHistorical),
            name: record.originalFileName,
          }),
        }}
        columns={columns}
        dataSource={simulationUploads?.map((up) => ({ ...up, key: up.s3Key }))}
      />
    </>
  );
};
